import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BookingChangeData, ChangeBookingPayload, ClubContactResponse, FinanceData, TravelInfo } from "@repo/ts";
import { useApiWithLanguage } from "./useApiWithLanguage";

const tournamentDetailsKeys = {
  travelInfo: (id: string) => ["tournament", id, "travelInfo"] as const,
  accommodation: (id: string) => ["tournament", id, "accommodation"] as const,
  booking: (id: string) => ["tournament", id, "booking"] as const,
  finance: (id: string) => ["tournament", id, "finance"] as const,
  guestLists: (id: string) => ["tournament", id, "guestLists"] as const,
  guestListDetail: (tournamentId: string, guestListId: string) => ["tournament", tournamentId, "guestList", guestListId] as const,
  contact: () => ["club", "contact"] as const,
  salesRepresentative: (id: string) => ["tournament", id, "salesRepresentative"] as const,
};

export function useTournamentTravelInfo(tournamentId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();

  return useQuery<TravelInfo>({
    queryKey: tournamentDetailsKeys.travelInfo(tournamentId),
    queryFn: async () => {
      const response = await fetchWithLanguage(`/api/tournaments/${tournamentId}/travel-info`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch travel info: ${response.statusText}`);
      }
      
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
    enabled: !!tournamentId,
  });
}

export function useTournamentAccommodation(tournamentId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();

  return useQuery({
    queryKey: tournamentDetailsKeys.accommodation(tournamentId),
    queryFn: async () => {
      const response = await fetchWithLanguage(`/api/tournaments/${tournamentId}/accommodation`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch accommodation: ${response.statusText}`);
      }
      
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
    enabled: !!tournamentId,
  });
}

export function useTournamentBooking(tournamentId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();

  return useQuery({
    queryKey: tournamentDetailsKeys.booking(tournamentId),
    queryFn: async () => {
      const response = await fetchWithLanguage(`/api/tournaments/${tournamentId}/booking`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch booking: ${response.statusText}`);
      }

      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
    enabled: !!tournamentId,
  });
}

export function useTournamentFinance(tournamentId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();

  return useQuery<FinanceData>({
    queryKey: tournamentDetailsKeys.finance(tournamentId),
    queryFn: async () => {
      const response = await fetchWithLanguage(`/api/tournaments/${tournamentId}/finance`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch finance: ${response.statusText}`);
      }
      
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
    enabled: !!tournamentId,
  });
}


export function useTournamentGuestLists(tournamentId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();

  return useQuery({
    queryKey: tournamentDetailsKeys.guestLists(tournamentId),
    queryFn: async () => {
      const response = await fetchWithLanguage(`/api/tournaments/${tournamentId}/guest-lists`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch guest lists: ${response.statusText}`);
      }
      
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
    enabled: !!tournamentId,
  });
}

export function useGuestListDetail(tournamentId: string, guestListId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();

  return useQuery({
    queryKey: tournamentDetailsKeys.guestListDetail(tournamentId, guestListId),
    queryFn: async () => {
      const response = await fetchWithLanguage(`/api/tournaments/${tournamentId}/guest-lists/${guestListId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch guest list detail: ${response.statusText}`);
      }
      
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
    enabled: !!tournamentId && !!guestListId,
  });
}

export function useClubContact() {
  const { fetchWithLanguage } = useApiWithLanguage();

  return useQuery<ClubContactResponse>({
    queryKey: tournamentDetailsKeys.contact(),
    queryFn: async (): Promise<ClubContactResponse> => {
      const response = await fetchWithLanguage(`/api/club/contact`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch contact: ${response.statusText}`);
      }
      
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useTournamentSalesRepresentative(tournamentId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();

  return useQuery({
    queryKey: tournamentDetailsKeys.salesRepresentative(tournamentId),
    queryFn: async () => {
      const response = await fetchWithLanguage(`/api/tournaments/${tournamentId}/sales-representative`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sales representative: ${response.statusText}`);
      }
      
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
    enabled: !!tournamentId,
  });
}

export function useBookingChangeData(tournamentId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();

  return {
    fetchChangeData: async (): Promise<BookingChangeData> => {
      const response = await fetchWithLanguage(
        `/api/tournaments/${tournamentId}/booking/data-needed-for-change`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch booking change data: ${response.statusText}`);
      }
      return response.json();
    },
  };
}

export function useChangeBooking(tournamentId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ChangeBookingPayload) => {
      const response = await fetchWithLanguage(`/api/tournaments/${tournamentId}/booking`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Failed to change booking: ${response.statusText}`);
      }
      return response.json().catch(() => ({}));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tournamentDetailsKeys.booking(tournamentId) });
    },
  });
}

export function useTournamentInvoicePdf(tournamentId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();

  return {
    downloadInvoice: async () => {
      try {
        const response = await fetchWithLanguage(`/api/tournaments/${tournamentId}/finance/invoice-pdf`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/pdf',
          },
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Invoice not available');
          }
          throw new Error(`Failed to download invoice: ${response.statusText}`);
        }

        // Get filename from response headers or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `invoice-${tournamentId}.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;]*=([^;]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['\"]/g, '');
          }
        }

        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

      } catch (error) {
        throw error;
      }
    },
  };
}